import React, { useEffect, useState, useMemo } from 'react';
import { Form, Button, Badge, InputGroup } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import LoaderOverlay from '../../Components/shared/Loader';
import AlertService from '../../services/utility/alertService';
import { getMyMasterRequests } from '../../services/api/masterRequestService';
import { getStatusConfig, getTypeLabel, REQUEST_STATUS } from './masterRequestConfig';
import { TypeBadge } from './DynamicFields';
import './MasterRequest.css';

const formatDate = (value) => {
    if (!value) return '-';
    const date = new Date(value);
    if (isNaN(date)) return '-';
    return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

const StatusBadge = ({ status }) => {
    const config = getStatusConfig(status);
    return <Badge bg={config.variant}>{config.label}</Badge>;
};

function MyMasterRequestPage() {
    const [requests, setRequests] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    const handleView = (reqId) => navigate(`/MyMasterRequestDetail/${reqId}`);

    useEffect(() => {
        const fetchRequests = async () => {
            try {
                const response = await getMyMasterRequests();
                setRequests(response.data || []);
            } catch (error) {
                console.error('Error fetching master requests:', error);
                AlertService.errorAlert('Unable to load your requests. Please try again.');
            } finally {
                setLoading(false);
            }
        };

        fetchRequests();
    }, []);

    const filteredData = useMemo(() => {
        const term = searchTerm.trim().toLowerCase();
        if (!term) return requests;

        // Search what is actually on screen, including the type label and the
        // formatted date, so typing "aug" or "item" matches what the user sees.
        return requests.filter((item) => [
            String(item.ID),
            item.REQ_NAME,
            getTypeLabel(item.REQ_TYPE),
            item.CUS_CODE,
            item.CUS_NAME,
            item.REQ_STATUS,
            item.CREATED_CODE,
            formatDate(item.REQ_DATE)
        ].some(field => (field || '').toString().toLowerCase().includes(term)));
    }, [requests, searchTerm]);

    return (
        <div className="card aon-card master-request-page">
            <LoaderOverlay loading={loading} message="Loading" height="500px" />
            <div className="card-header aon-card-header">
                <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                    <h4 className="mb-0">My Customer/Item Requests</h4>
                    <Button variant="primary" onClick={() => navigate('/NewMasterRequest')}>
                        New Request
                    </Button>
                </div>
            </div>

            <div className="card-body aon-card-body">
                {/* Search sits above the table rather than inside a table row, so it
                    stays reachable instead of scrolling away horizontally on a phone. */}
                <InputGroup className="mb-3">
                    <Form.Control
                        placeholder="Search requests..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        aria-label="Search requests"
                    />
                    {searchTerm && (
                        <Button variant="outline-secondary" onClick={() => setSearchTerm('')}>
                            Clear
                        </Button>
                    )}
                </InputGroup>

                {filteredData.length === 0 ? (
                    <div className="text-center text-muted py-5">
                        {requests.length === 0
                            ? 'You have not raised any Customer/Item requests yet.'
                            : 'No requests match your search.'}
                    </div>
                ) : (
                    <>
                        {/* Phone: one card per request. A seven column table cannot be
                            read on a 390px screen even with horizontal scrolling. */}
                        <div className="d-md-none">
                            {filteredData.map((item) => (
                                <div
                                    className="mrq-card mrq-card-link"
                                    key={item.ID}
                                    role="button"
                                    tabIndex={0}
                                    onClick={() => handleView(item.ID)}
                                    onKeyDown={(e) => { if (e.key === 'Enter') handleView(item.ID); }}
                                >
                                    <div className="d-flex justify-content-between align-items-start gap-2">
                                        <div className="mrq-card-title">{item.REQ_NAME}</div>
                                        <StatusBadge status={item.REQ_STATUS} />
                                    </div>
                                    <div className="mrq-card-meta">
                                        #{item.ID} &middot; <TypeBadge reqType={item.REQ_TYPE} /> &middot; {formatDate(item.REQ_DATE)}
                                    </div>
                                    {item.CUS_NAME && (
                                        <div className="mrq-card-meta">Customer: {item.CUS_NAME}</div>
                                    )}
                                    {item.CREATED_CODE && (
                                        <div className="mrq-card-created">
                                            Created: {item.CREATED_CODE}
                                            {item.CREATED_NAME ? ' - ' + item.CREATED_NAME : ''}
                                        </div>
                                    )}
                                    {item.REQ_STATUS === REQUEST_STATUS.Rejected && item.ACTION_COMMENT && (
                                        <div className="mrq-card-reason">Reason: {item.ACTION_COMMENT}</div>
                                    )}
                                </div>
                            ))}
                        </div>

                        {/* Desktop */}
                        <div className="d-none d-md-block sf-bs-data-table">
                            <div className="table-responsive">
                                <table
                                    className="table table-striped table-bordered aon-booking-table"
                                    style={{ width: '100%' }}
                                >
                                    <thead>
                                        <tr>
                                            <th>#</th>
                                            <th>Date</th>
                                            <th>Type</th>
                                            <th>Name</th>
                                            <th>Customer</th>
                                            <th>Created Code</th>
                                            <th>Status</th>
                                            <th>Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredData.map((item) => (
                                            <tr key={item.ID}>
                                                <td>{item.ID}</td>
                                                <td>{formatDate(item.REQ_DATE)}</td>
                                                <td><TypeBadge reqType={item.REQ_TYPE} /></td>
                                                <td>{item.REQ_NAME}</td>
                                                <td>{item.CUS_NAME || '-'}</td>
                                                <td>{item.CREATED_CODE || '-'}</td>
                                                <td><StatusBadge status={item.REQ_STATUS} /></td>
                                                <td>
                                                    <Button
                                                        variant="outline-primary"
                                                        size="sm"
                                                        onClick={() => handleView(item.ID)}
                                                    >
                                                        View
                                                    </Button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}

export default MyMasterRequestPage;
